import { useCallback, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { ArrowLeft, FileSpreadsheet } from "lucide-react";
import { format } from "date-fns";
import toast from "react-hot-toast";
import DataTable, { Column } from "@/components/ui/DataTable";
import OrderDetailsDrawer from "@/components/order/OrderDetailsDrawer";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { DateRangePicker } from "@/components/Dashboard/DateRangePicker";
import { SearchIgnoresFiltersHint } from "@/components/common/SearchIgnoresFiltersHint";
import { filtersUnlessSearching } from "@/lib/searchScope";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  useGetRestaurantPaymentsQuery,
  useLazyGetRestaurantPaymentsCsvQuery,
} from "@/redux/services/pos/payment.api";
import {
  PaymentListRow,
  PaymentMethodName,
  PaymentStatus,
  RestaurantPaymentsExportQuery,
} from "@/redux/services/pos/pos.type";
import { useAppSelector } from "@/redux/features/hooks";
import {
  displayPrice,
  formatDate,
  getPaymentMethodLabel,
} from "@/lib/utils";
import { downloadBlob } from "@/lib/reportDownload";
import { useRestaurantCurrency } from "@/lib/hooks";
import PageTitle from "@/components/PageTitle";
import RoleProtectedPage from "@/layouts/RoleProtectedPage";
import DashLayout from "@/layouts/DashLayout";
import { Roles } from "@/redux/services/user/user.type";
import { useDebounce } from "@/hooks/useDebounce";

// Column key -> API order[field] sort target.
const SORT_MAP: Record<string, "order[amount]" | "order[processedAt]"> = {
  amount: "order[amount]",
  processedAt: "order[processedAt]",
};

const METHOD_OPTIONS: PaymentMethodName[] = [
  "cash",
  "card",
  "mobile money",
  "wave",
  "orange money",
];

const STATUS_LABELS: Record<PaymentStatus, string> = {
  success: "Réussi",
  pending: "En attente",
  failed: "Échoué",
};

// Sentinel used by the "all" option — Radix Select forbids an empty value.
const ALL = "all";

export default function ReportsPaymentsPage() {
  const [searchParams] = useSearchParams();
  const currency = useRestaurantCurrency();
  const currentRestaurant = useAppSelector(
    (state) => state.user.currentRestaurant
  );

  // The page owns its own filters. The date range defaults to the one carried
  // over from the reports page (URL query string), falling back to today — the
  // same default the reports page itself uses — then the user can change it
  // here with the shared date picker.
  const today = format(new Date(), "yyyy-MM-dd");
  const initialMethod =
    (searchParams.get("method") as PaymentMethodName | null) ?? null;

  const [startDate, setStartDate] = useState(
    searchParams.get("start") ?? today
  );
  const [endDate, setEndDate] = useState(searchParams.get("end") ?? today);
  const [method, setMethod] = useState<PaymentMethodName | typeof ALL>(
    initialMethod ?? ALL
  );
  const [status, setStatus] = useState<PaymentStatus | typeof ALL>(ALL);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchQuery, setSearchQuery] = useState("");
  const [sortBy, setSortBy] = useState<string>("");
  const [sortColumn, setSortColumn] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");
  const [selectedOrder, setSelectedOrder] = useState<{
    id: string;
    number: string;
  } | null>(null);

  const debouncedSearchQuery = useDebounce(searchQuery, 500);

  // Shared filter set — the "all money that moved" ledger, so no status is
  // pinned by default (cancelled-order / non-SUCCESS payments are included
  // unless the user narrows the status filter). Reused verbatim by the export.
  const filters: RestaurantPaymentsExportQuery = useMemo(
    () => ({
      // A search term looks across all dates and ignores the method and
      // status filters; the filters scope browsing only.
      ...filtersUnlessSearching(debouncedSearchQuery, {
        ...(method !== ALL && { paymentMethod: method }),
        ...(status !== ALL && { status }),
        ...(startDate && { "processedAt[after]": `${startDate}T00:00:00` }),
        ...(endDate && { "processedAt[before]": `${endDate}T23:59:59` }),
      }),
      ...(debouncedSearchQuery && { q: debouncedSearchQuery }),
    }),
    [method, status, startDate, endDate, debouncedSearchQuery]
  );

  const queryArgs = useMemo(
    () => ({
      page: currentPage,
      limit: pageSize,
      ...filters,
      ...(sortBy && { [sortBy]: sortOrder }),
    }),
    [currentPage, pageSize, filters, sortBy, sortOrder]
  );

  const { data, isLoading, isFetching, isError, refetch } =
    useGetRestaurantPaymentsQuery(queryArgs, { skip: !currentRestaurant?.id });

  const [triggerCsv, { isFetching: isCsvLoading }] =
    useLazyGetRestaurantPaymentsCsvQuery();

  const handlePageChange = useCallback((page: number) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size: number) => {
    setPageSize(size);
    setCurrentPage(1);
  }, []);

  const handleSearch = useCallback((value: string) => {
    setSearchQuery(value);
    setCurrentPage(1);
  }, []);

  const handleSort = useCallback((column: string, order: "asc" | "desc") => {
    setSortBy(SORT_MAP[column] ?? "");
    setSortColumn(column);
    setSortOrder(order);
    setCurrentPage(1);
  }, []);

  const handleMethodChange = useCallback((value: string) => {
    setMethod(value as PaymentMethodName | typeof ALL);
    setCurrentPage(1);
  }, []);

  const handleStatusChange = useCallback((value: string) => {
    setStatus(value as PaymentStatus | typeof ALL);
    setCurrentPage(1);
  }, []);

  const handleDateChange = useCallback(
    (newStartDate: string, newEndDate: string) => {
      setStartDate(newStartDate);
      setEndDate(newEndDate);
      setCurrentPage(1);
    },
    []
  );

  const handleDownloadCsv = useCallback(async () => {
    try {
      const { blob, filename } = await triggerCsv(filters).unwrap();
      downloadBlob(blob, filename ?? "paiements.csv");
    } catch {
      toast.error("Erreur lors du téléchargement du CSV");
    }
  }, [triggerCsv, filters]);

  const columns: Column[] = [
    {
      key: "processedAt",
      label: "Date",
      sortable: true,
      type: "date",
      nowrap: true,
      formatter: (_, row: PaymentListRow) =>
        formatDate(new Date(row.processedAt), true, true),
    },
    {
      key: "paymentMethod",
      label: "Moyen",
      formatter: (_, row: PaymentListRow) => (
        <span className="capitalize">
          {getPaymentMethodLabel(row.paymentMethod)}
        </span>
      ),
    },
    {
      key: "amount",
      label: "Montant",
      sortable: true,
      type: "number",
      nowrap: true,
      formatter: (_, row: PaymentListRow) => (
        <span className="font-medium tabular-nums">
          {displayPrice(row.amount, currency)}
        </span>
      ),
    },
    {
      key: "tipAmount",
      label: "Pourboire",
      type: "number",
      nowrap: true,
      formatter: (_, row: PaymentListRow) =>
        row.tipAmount ? displayPrice(row.tipAmount, currency) : "-",
    },
    {
      key: "status",
      label: "Statut",
      formatter: (_, row: PaymentListRow) => (
        <Badge variant={row.status}>{STATUS_LABELS[row.status]}</Badge>
      ),
    },
    {
      key: "orderNumber",
      label: "Commande",
      nowrap: true,
      formatter: (_, row: PaymentListRow) =>
        row.order?.id ? (
          <button
            type="button"
            onClick={() =>
              setSelectedOrder({
                id: row.order.id,
                number: row.order.orderNumber,
              })
            }
            className="font-medium text-primary hover:underline"
          >
            {row.order.orderNumber}
          </button>
        ) : (
          "-"
        ),
    },
    {
      key: "customer",
      label: "Client",
      formatter: (_, row: PaymentListRow) => {
        const customer = row.order?.customer;
        if (!customer?.fullName) return "-";
        const name = customer.fullName;
        return customer.id ? (
          <Link
            to={`/customers/${customer.id}`}
            className="text-primary hover:underline"
          >
            {name}
          </Link>
        ) : (
          name
        );
      },
    },
    {
      key: "reference",
      label: "Référence",
      nowrap: true,
      formatter: (_, row: PaymentListRow) => row.transactionReference || "-",
    },
    {
      key: "processedBy",
      label: "Encaissé par",
      formatter: (_, row: PaymentListRow) => row.processedBy?.fullName ?? "-",
    },
  ];

  const periodLabel =
    startDate && endDate
      ? `du ${formatDate(new Date(startDate), false, true)} au ${formatDate(
          new Date(endDate),
          false,
          true
        )}`
      : "sur toute la période";

  return (
    <DashLayout>
      <RoleProtectedPage
        allowedRoles={[Roles.manager, Roles.owner]}
        allowedPermissions={["reports.view"]}
      >
        <div className="space-y-6">
          <div className="space-y-2">
            <Link
              to="/reports"
              className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
            >
              <ArrowLeft className="h-4 w-4" />
              Retour au rapport
            </Link>
            <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
              <PageTitle
                title="Journal des paiements"
                description={`Tous les paiements encaissés ${periodLabel} — encaissements, pourboires et transactions par moyen de paiement.`}
              />
              <Button
                onClick={handleDownloadCsv}
                disabled={isCsvLoading || !currentRestaurant}
                isLoading={isCsvLoading}
                size="sm"
                variant="outline"
                className="border-[#066a69]/40 text-[#066a69] hover:bg-[#066a69]/10 hover:text-[#066a69] shrink-0"
              >
                <FileSpreadsheet className="h-4 w-4 mr-2" />
                Télécharger CSV
              </Button>
            </div>
          </div>

          {!currentRestaurant ? (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="text-center bg-yellow-50 border-l-4 border-yellow-800 rounded py-4">
                Veuillez sélectionner un restaurant
              </div>
            </div>
          ) : (
            <>
              <div className="space-y-3">
                {/* Date picker on its own row — its dropdown panel is wide and
                    would overlap the method/status selects if they shared a
                    line. */}
                <div>
                  <DateRangePicker
                    startDate={startDate}
                    endDate={endDate}
                    onDateChange={handleDateChange}
                  />
                </div>
                <SearchIgnoresFiltersHint search={debouncedSearchQuery} />

                <div className="flex flex-wrap items-center gap-3">
                  <Select value={method} onValueChange={handleMethodChange}>
                    <SelectTrigger className="w-full sm:w-56">
                      <SelectValue placeholder="Moyen de paiement" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>Tous les moyens</SelectItem>
                      {METHOD_OPTIONS.map((m) => (
                        <SelectItem key={m} value={m}>
                          {getPaymentMethodLabel(m)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>

                  <Select value={status} onValueChange={handleStatusChange}>
                    <SelectTrigger className="w-full sm:w-48">
                      <SelectValue placeholder="Statut" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={ALL}>Tous les statuts</SelectItem>
                      {(Object.keys(STATUS_LABELS) as PaymentStatus[]).map(
                        (s) => (
                          <SelectItem key={s} value={s}>
                            {STATUS_LABELS[s]}
                          </SelectItem>
                        )
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {isError ? (
                // A failed fetch must not fall through to the empty table — on a
                // money ledger that would read as "no payments" instead of an
                // error. Surface it explicitly and let the user retry.
                <div className="bg-card rounded-lg border border-border p-6">
                  <div className="flex flex-col items-center gap-3 py-8 text-center">
                    <p className="text-destructive">
                      Une erreur s'est produite lors du chargement des paiements.
                    </p>
                    <Button variant="outline" size="sm" onClick={() => refetch()}>
                      Réessayer
                    </Button>
                  </div>
                </div>
              ) : (
                <DataTable
                  data={data?.results || []}
                  columns={columns}
                  loading={isLoading || isFetching}
                  totalCount={data?.count || 0}
                  currentPage={currentPage}
                  pageSize={pageSize}
                  sortBy={sortColumn}
                  sortOrder={sortOrder}
                  searchValue={searchQuery}
                  onSearch={handleSearch}
                  onSort={handleSort}
                  onPageChange={handlePageChange}
                  onPageSizeChange={handlePageSizeChange}
                  searchPlaceholder="Commande, client, téléphone ou référence..."
                  emptyMessage="Aucun paiement pour ces filtres sur la période."
                />
              )}
            </>
          )}

          {selectedOrder && (
            <OrderDetailsDrawer
              orderId={selectedOrder.id}
              orderNumber={selectedOrder.number}
              isOpen={!!selectedOrder}
              onClose={() => setSelectedOrder(null)}
            />
          )}
        </div>
      </RoleProtectedPage>
    </DashLayout>
  );
}
